/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-underscore-dangle */
import React, { useContext } from 'react';
import { StopOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Col, Row, message, Divider, Input } from 'antd';
import { useMutation } from '@apollo/react-hooks';
import {
  InvoiceContext,
  SelectedInvoiceDetailType,
} from 'views/AccountReceive/common/context/InvoiceContext';
import UPDATE_INVOICE_MUTATION, {
  UpdateInvoiceVars,
  UpdateInvoiceData,
} from 'shared/graphql/mutation/updateInvoice';
import formatCurrency from 'shared/utils/formatCurrency';
import { Button, Text } from 'shared/components';
import { formatDate } from 'shared/utils/dateTime';
import { useFormContext, Controller } from 'react-hook-form';
import { FormValues } from 'views/AccountReceive/Routes/Invoice/InvoicesTable/ModalInvoiceDetail/model/formValues.model';
import { StyledModal } from './Styles';

type ModalConfirmCancelProps = {
  visible: boolean;
  onCancel: () => void;
};

const ModalConfirmCancel: React.FC<ModalConfirmCancelProps> = (props) => {
  const { onCancel, visible } = props;
  const { selectedInvoiceDetail, setSelectedInvoiceDetail } = useContext(InvoiceContext);
  const { modalConfirmCreateInvoice, invoiceId } = selectedInvoiceDetail;
  const {
    payerFullName,
    businessInvoiceType,
    grandTotalPrice,
    costings,
  } = modalConfirmCreateInvoice;

  const { watch, reset, control } = useFormContext<FormValues>();
  const watchCancelRemark = watch('cancelRemark');

  const closeModalHandler = () => {
    setSelectedInvoiceDetail(
      (prevState): SelectedInvoiceDetailType => ({
        ...prevState,
        isOpenModal: false,
      }),
    );
  };

  const [updateInvoice, { loading }] = useMutation<UpdateInvoiceData, UpdateInvoiceVars>(
    UPDATE_INVOICE_MUTATION,
    {
      update: (_cache, { data }) => {
        if (data?.updateInvoice._id) {
          message.success('เปลี่ยนสถานะใบแจ้งหนี้แล้ว');
          onCancel();
          reset();
          closeModalHandler();
        }
      },
      onError: (err) => {
        message.error(err.message);
      },
      refetchQueries: () => ['Invoice', 'InvoiceStatus', 'Invoices'],
    },
  );

  const confirmCancel = () => {
    updateInvoice({
      variables: {
        _id: invoiceId,
        invoice_status: 5,
        cancel_remark: watchCancelRemark,
      },
    });
  };

  return (
    <StyledModal
      className="custom-modal"
      title="ยกเลิกการใช้ใบแจ้งหนี้หรือไม่?"
      visible={visible}
      onCancel={onCancel}
      footer={[
        <Button
          key="okButton"
          icon={<StopOutlined />}
          color="red"
          onClick={() => confirmCancel()}
          loading={loading}
          disabled={!watchCancelRemark || loading}
        >
          ยกเลิกการใช้
        </Button>,
        <Button
          key="cancelButton"
          icon={<CloseCircleOutlined />}
          onClick={onCancel}
          disabled={loading}
        >
          ยกเลิก
        </Button>,
      ]}
    >
      <Row>
        <Col span={6}>
          ลูกค้า <Text customType="grey">(ลูกหนี้)</Text>:
        </Col>
        <Col span={18}>
          <Text strong>{payerFullName}</Text>
        </Col>
      </Row>
      <Row>
        <Col span={6}>รูปแบบรับใบแจ้งหนี้:</Col>
        <Col span={18}>
          <Text strong>{businessInvoiceType}</Text>
        </Col>
      </Row>
      <Row>
        <Col span={6}>มูลค่าใบแจ้งหนี้รวม:</Col>
        <Col span={18}>
          <Text strong customType="primary">
            {formatCurrency(grandTotalPrice, '0')}
          </Text>
        </Col>
      </Row>

      <Divider style={{ marginTop: 10, marginBottom: 10 }} />

      <Row>
        <Col span={24}>
          ทั้งหมด <Text strong>{`${costings.length} รายการ`}</Text>
        </Col>
        <Col span={24}>
          <Row gutter={[8, 0]}>
            <Col span={4}>วันที่อนุมัติ</Col>
            <Col span={4}>รหัสต้นทุน/ขาย</Col>
            <Col span={4}>รหัสงานขนส่ง</Col>
            <Col span={4}>วันที่เริ่มงาน</Col>
            <Col span={4}>วันที่งานเสร็จสิ้น</Col>
            <Col span={4}>ราคาขาย</Col>
          </Row>

          {costings.map((costing) => (
            <Row gutter={[8, 0]}>
              <Col span={4}>
                <Text strong>{costing.checkedDate ? formatDate(costing.checkedDate) : '-'}</Text>
              </Col>
              <Col span={4}>
                <Text strong>{costing.costingNumber || '-'}</Text>
              </Col>
              <Col span={4}>
                <Text strong>{costing.numberRef}</Text>
              </Col>
              <Col span={4}>
                <Text strong>{costing.serviceDate ? formatDate(costing.serviceDate) : '-'}</Text>
              </Col>
              <Col span={4}>
                <Text strong>
                  {costing.completedDate ? formatDate(costing.completedDate) : '-'}
                </Text>
              </Col>
              <Col span={4}>
                <Text strong customType="primary">
                  {formatCurrency(costing.grandTotalPrice)}
                </Text>
              </Col>
            </Row>
          ))}
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          เหตุผล:<Text type="danger">*</Text>
        </Col>
        <Col span={12}>
          <Controller
            as={Input.TextArea}
            name="cancelRemark"
            control={control}
            placeholder="ระบุเหตุผล"
          />
        </Col>
      </Row>
    </StyledModal>
  );
};

export default ModalConfirmCancel;
